import { doc, runTransaction, collection, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { calculateSellerAmount } from './commissionUtils';

interface TransactionResult {
  success: boolean;
  error?: string;
}

export async function processOrderCancellation(
  orderId: string,
  buyerId: string | undefined,
  sellerId: string | undefined,
  orderTotal: number,
  orderSubtotal: number,
  shippingCost: number,
  commission: number,
  isManualOrder: boolean,
  isBuyerRequested: boolean
): Promise<TransactionResult> {
  try {
    // Get effective seller ID - try multiple sources
    const effectiveSellerId = sellerId || await getEffectiveSellerId(orderId);
    
    if (!effectiveSellerId) {
      throw new Error('ID do vendedor não encontrado. Verifique se o ID da empresa está definido na encomenda.');
    }

    let result: TransactionResult = { success: false };

    await runTransaction(db, async (transaction) => {
      const timestamp = new Date().toISOString();

      // For manual orders, just handle commission if needed
      if (isManualOrder) {
        // Check if seller is exempt from commissions
        const sellerDoc = await getDoc(doc(db, 'users', effectiveSellerId));
        const isCommissionExempt = sellerDoc.exists() && sellerDoc.data().commissionExempt;

        // Only handle commission if seller is not exempt and there is a commission
        if (!isCommissionExempt && commission > 0) {
          // If buyer requested cancellation, refund/cancel commission
          if (isBuyerRequested) {
            // Create commission refund transaction
            const commissionRefundRef = doc(collection(db, 'walletTransactions'));
            transaction.set(commissionRefundRef, {
              type: 'commission_refund',
              amount: commission,
              baseAmount: commission / 1.23, // Remove 23% VAT
              vatAmount: commission - (commission / 1.23),
              status: 'completed',
              description: `Reembolso de comissão - Cancelamento solicitado pelo comprador - ${orderId}`,
              companyId: effectiveSellerId,
              orderId,
              createdAt: timestamp
            });
          }
          // If seller cancels, commission is kept as penalty - no transaction needed
        }

        result = { success: true };
        return;
      }

      // For marketplace and private sales
      if (!buyerId) {
        throw new Error('ID do comprador não encontrado');
      }

      // Get wallet references
      const buyerWalletRef = doc(db, 'wallets', buyerId);
      const sellerWalletRef = doc(db, 'wallets', effectiveSellerId);
      
      const [buyerWalletDoc, sellerWalletDoc] = await Promise.all([
        transaction.get(buyerWalletRef),
        transaction.get(sellerWalletRef)
      ]);
      
      if (!buyerWalletDoc.exists()) {
        throw new Error('Carteira do comprador não encontrada');
      }
      if (!sellerWalletDoc.exists()) {
        throw new Error('Carteira do vendedor não encontrada');
      }

      const buyerBalance = buyerWalletDoc.data().balance || 0;
      const sellerBalance = sellerWalletDoc.data().balance || 0;

      // Calculate amounts
      const { sellerAmount } = await calculateSellerAmount(
        orderSubtotal,
        effectiveSellerId,
        false // Always false for marketplace/private sales
      );

      const buyerRefund = orderTotal;
      const sellerDeduction = sellerAmount;

      // Check if seller is exempt from commissions
      const sellerDoc = await transaction.get(doc(db, 'users', effectiveSellerId));
      const isCommissionExempt = sellerDoc.exists() && sellerDoc.data().commissionExempt;
      const commissionRefund = isBuyerRequested && !isCommissionExempt ? commission : 0;

      // Update wallets
      transaction.update(buyerWalletRef, {
        balance: buyerBalance + buyerRefund,
        updatedAt: timestamp
      });

      transaction.update(sellerWalletRef, {
        balance: sellerBalance - sellerDeduction + commissionRefund,
        updatedAt: timestamp
      });

      // Create transactions
      const buyerRefundRef = doc(collection(db, 'walletTransactions'));
      transaction.set(buyerRefundRef, {
        type: 'refund',
        amount: buyerRefund,
        status: 'completed',
        description: `Reembolso total - ${orderId}`,
        companyId: buyerId,
        orderId,
        createdAt: timestamp
      });

      const sellerDeductionRef = doc(collection(db, 'walletTransactions'));
      transaction.set(sellerDeductionRef, {
        type: 'deduction',
        amount: -sellerDeduction,
        status: 'completed',
        description: `Estorno do valor recebido - ${orderId}`,
        companyId: effectiveSellerId,
        orderId,
        createdAt: timestamp
      });

      // Handle commission refund if applicable
      if (commissionRefund > 0) {
        const commissionRefundRef = doc(collection(db, 'walletTransactions'));
        transaction.set(commissionRefundRef, {
          type: 'commission_refund',
          amount: commissionRefund,
          baseAmount: commissionRefund / 1.23, // Remove 23% VAT
          vatAmount: commissionRefund - (commissionRefund / 1.23),
          status: 'completed',
          description: `Reembolso de comissão - Cancelamento solicitado pelo comprador - ${orderId}`,
          companyId: effectiveSellerId,
          orderId,
          createdAt: timestamp
        });
      }

      result = { success: true };
    });

    return result;
  } catch (error) {
    console.error('Error processing order cancellation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao processar cancelamento'
    };
  }
}

// Helper function to get effective seller ID from order
async function getEffectiveSellerId(orderId: string): Promise<string | undefined> {
  try {
    const orderDoc = await getDoc(doc(db, 'orders', orderId));
    if (!orderDoc.exists()) {
      console.error('Order not found:', orderId);
      return undefined;
    }

    const orderData = orderDoc.data();
    
    // Try to get seller ID in this order:
    // 1. From order's sellerId
    // 2. From order's companyId
    // 3. From first item's companyId
    const potentialSellerId = orderData.sellerId || 
                            orderData.companyId || 
                            (orderData.items?.[0]?.companyId);

    if (!potentialSellerId) {
      console.error('No seller or company ID found in order:', orderId, orderData);
      return undefined;
    }

    // Verify the company exists
    const companyDoc = await getDoc(doc(db, 'companies', potentialSellerId));
    if (!companyDoc.exists()) {
      console.error('Company not found:', potentialSellerId);
      return undefined;
    }

    return potentialSellerId;
  } catch (error) {
    console.error('Error getting effective seller ID:', error);
    return undefined;
  }
}